/**
 * Session Manager
 *
 * Shared component for writers and broadcasters to create or join live sessions.
 * Shows a "Create New Session" form and an "Active Sessions" list.
 */

import { FileText, Monitor, Play, RefreshCw, Users, Video } from 'lucide-react'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { trpc } from '../trpc/client'
import { Alert, Button, IconButton, Spinner, TextField } from './ui'

type SessionRole = 'WRITER' | 'BROADCASTER'

interface SessionManagerProps {
	currentRole: SessionRole
	roomId?: string
	onSessionReady: (documentId: string, title: string) => void
}

export function SessionManager({ currentRole, roomId, onSessionReady }: SessionManagerProps) {
	const { t } = useTranslation()
	const [title, setTitle] = useState('')

	const createMutation = trpc.liveSessions.create.useMutation({
		onSuccess: (data) => {
			onSessionReady(data.documentId, data.title)
		},
	})

	const joinMutation = trpc.liveSessions.join.useMutation({
		onSuccess: (data) => {
			onSessionReady(data.documentId, data.title)
		},
	})

	const sessionsQuery = trpc.liveSessions.list.useQuery(undefined, {
		refetchInterval: 5000,
	})

	const handleCreate = useCallback(() => {
		if (!title.trim()) return
		createMutation.mutate({ title: title.trim(), role: currentRole, roomId })
	}, [title, currentRole, roomId, createMutation])

	const handleJoin = useCallback(
		(documentId: string) => {
			joinMutation.mutate({ documentId, role: currentRole })
		},
		[currentRole, joinMutation],
	)

	const isLoading = createMutation.isPending || joinMutation.isPending

	return (
		<div className="space-y-6">
			{/* Create New Session */}
			<div className="card-refined p-6 space-y-4">
				<h2 className="text-lg font-semibold">{t('sessions.create.title')}</h2>

				{/* The create error is wired to the field (aria-invalid + role=alert
				    via the TextField primitive) so screen readers announce it. */}
				<TextField
					label={t('sessions.create.fields.title')}
					placeholder={
						currentRole === 'WRITER'
							? t('sessions.manager.titlePlaceholderWriter')
							: t('sessions.manager.titlePlaceholderBroadcaster')
					}
					value={title}
					onChange={(e) => setTitle(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === 'Enter') handleCreate()
					}}
					required
					disabled={isLoading}
					error={createMutation.error?.message}
				/>

				<Button
					onClick={handleCreate}
					disabled={!title.trim()}
					loading={isLoading}
					leadingIcon={<Play size={16} />}
				>
					{t('sessions.manager.start')}
				</Button>
			</div>

			{/* Active Sessions */}
			<div className="card-refined p-6 space-y-4">
				<div className="flex items-center justify-between">
					<h2 className="text-lg font-semibold">{t('sessions.manager.activeTitle')}</h2>
					<IconButton
						variant="ghost"
						size="sm"
						onClick={() => sessionsQuery.refetch()}
						aria-label={t('sessions.list.refresh')}
					>
						<RefreshCw className={sessionsQuery.isFetching ? 'animate-spin' : ''} />
					</IconButton>
				</div>

				{joinMutation.error && <Alert tone="error">{joinMutation.error.message}</Alert>}

				{sessionsQuery.isLoading ? (
					<div className="flex items-center justify-center py-8 text-base-content/40">
						<Spinner size="lg" />
					</div>
				) : sessionsQuery.isError ? (
					<Alert tone="error">{t('sessions.list.errors.loadFailed')}</Alert>
				) : !sessionsQuery.data?.length ? (
					<p className="text-sm text-base-content/50 py-4 text-center">
						{t('sessions.list.empty.title')}
					</p>
				) : (
					<div className="space-y-3">
						{sessionsQuery.data.map((session) => (
							<div
								key={session.id}
								className="flex items-center justify-between p-3 rounded-lg border border-base-300 bg-base-100"
							>
								<div className="flex-1 min-w-0">
									<div className="font-medium truncate">{session.title}</div>
									<div className="flex items-center gap-3 mt-1">
										<span className="text-xs text-base-content/50">
											{t('sessions.list.byCreator', {
												name: session.createdBy.name ?? t('sessions.join.unknownCreator'),
											})}
										</span>
										<div className="flex items-center gap-2">
											{session.hasTranscript && (
												<span className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
													<FileText size={10} aria-hidden="true" />
													{t('sessions.list.badges.transcript')}
												</span>
											)}
											{session.hasVideo && (
												<span className="inline-flex items-center gap-1 text-xs bg-accent/10 text-coral-700 px-2 py-0.5 rounded-full">
													<Video size={10} aria-hidden="true" />
													{t('sessions.list.badges.video')}
												</span>
											)}
											{session.hasScreencast && (
												<span className="inline-flex items-center gap-1 text-xs bg-amber-500/10 text-amber-700 px-2 py-0.5 rounded-full">
													<Monitor size={10} aria-hidden="true" />
													{t('sessions.list.badges.screencast')}
												</span>
											)}
										</div>
									</div>
								</div>

								<Button
									variant="ghost"
									size="sm"
									onClick={() => handleJoin(session.documentId)}
									disabled={isLoading}
									leadingIcon={<Users size={14} />}
									className="text-primary ml-3 shrink-0"
								>
									{t('sessions.list.join')}
								</Button>
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	)
}
